/**
 * Admin Plan Health Dashboard
 *
 * Sunday-morning weekend validation for the two critical plan pipelines:
 *  - Weekly Plan Synthesizer Health — surfaces fallback-triggered and
 *    validation-failed weekly plans from the overnight cron
 *  - Cedar Report Health — surfaces Cedar reports that used the fallback
 *    path or landed with zero intelligence engines active
 *
 * Uses the same admin-token Bearer fetch pattern as AdminSystemAlerts.
 * Added: Apr 19, 2026 — see plan file melodic-seeking-island.md §3c.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { storage } from '../services/storage';
import {
  AlertTriangle,
  ClipboardList,
  FileText,
  RefreshCw,
  Users,
  Activity,
  Clock,
} from 'lucide-react';
import { API_BASE_URL } from '../utils/apiConfig';

// ── API helper ───────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string): Promise<T> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found. Please log in again.');

  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

type TabId = 'weekly-plan-synth' | 'cedar-report';

interface WeeklyPlanSynthAlert {
  id: string;
  title: string;
  message: string;
  severity: string;
  status: string;
  created_at: string | null;
  user_id: string | null;
  plan_id: string | null;
  fallback_source: string | null;
  validation_issues: string[];
}

interface WeeklyPlanSynthResponse {
  success: boolean;
  summary: {
    total_alerts: number;
    users_affected: number;
    fallback_triggered_count: number;
    period_days: number;
  };
  alerts: WeeklyPlanSynthAlert[];
}

interface CedarReportAlert {
  id: string;
  title: string;
  message: string;
  severity: string;
  status: string;
  created_at: string | null;
  user_id: string | null;
  reason: string;
  detail: string;
  engines_active: string[];
}

interface CedarReportResponse {
  success: boolean;
  summary: {
    total_alerts: number;
    users_affected: number;
    reason_breakdown: Record<string, number>;
    period_days: number;
  };
  alerts: CedarReportAlert[];
}

// ── Components ───────────────────────────────────────────────────────────────

const SummaryTile: React.FC<{ label: string; value: string | number; icon: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="text-muted-foreground">{icon}</div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </CardContent>
  </Card>
);

const AlertRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border rounded-lg p-4 mb-2 bg-card">{children}</div>
);

// ── Weekly Plan Synth tab ────────────────────────────────────────────────────

const WeeklyPlanSynthTab: React.FC<{ days: number }> = ({ days }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WeeklyPlanSynthResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<WeeklyPlanSynthResponse>(
        `/api/admin/system/alerts/weekly-plan-synth-health?days=${days}`,
      );
      setData(res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="p-4 text-muted-foreground">Loading weekly plan synth health…</div>;
  if (error) return <div className="p-4 text-destructive">Error: {error}</div>;
  if (!data) return null;

  const { summary, alerts } = data;

  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <SummaryTile label="Total alerts" value={summary.total_alerts} icon={<AlertTriangle size={20} />} />
        <SummaryTile label="Users affected" value={summary.users_affected} icon={<Users size={20} />} />
        <SummaryTile
          label="Template fallbacks"
          value={summary.fallback_triggered_count}
          icon={<RefreshCw size={20} />}
        />
        <SummaryTile label="Period (days)" value={summary.period_days} icon={<Clock size={20} />} />
      </div>

      <div>
        {alerts.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground border rounded-lg">
            No alerts in the last {days} days. Weekly plan synth is healthy. 🌲
          </div>
        ) : (
          alerts.map((a) => (
            <AlertRow key={a.id}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-sm text-muted-foreground">{a.message}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.created_at ? new Date(a.created_at).toLocaleString() : 'unknown time'}
                </div>
              </div>
              <div className="text-xs space-y-1">
                <div>
                  <span className="font-semibold">User:</span> {a.user_id || '—'}
                </div>
                <div>
                  <span className="font-semibold">Plan ID:</span> {a.plan_id || '—'}
                </div>
                {a.fallback_source && (
                  <div>
                    <span className="font-semibold">Fallback:</span> {a.fallback_source}
                  </div>
                )}
                {a.validation_issues && a.validation_issues.length > 0 && (
                  <div>
                    <span className="font-semibold">Validation issues ({a.validation_issues.length}):</span>
                    <ul className="list-disc ml-5 mt-1">
                      {a.validation_issues.slice(0, 10).map((iss, idx) => (
                        <li key={idx}>{iss}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertRow>
          ))
        )}
      </div>
    </div>
  );
};

// ── Cedar Report tab ─────────────────────────────────────────────────────────

const CedarReportTab: React.FC<{ days: number }> = ({ days }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CedarReportResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<CedarReportResponse>(
        `/api/admin/system/alerts/cedar-report-health?days=${days}`,
      );
      setData(res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="p-4 text-muted-foreground">Loading Cedar report health…</div>;
  if (error) return <div className="p-4 text-destructive">Error: {error}</div>;
  if (!data) return null;

  const { summary, alerts } = data;

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <SummaryTile label="Total alerts" value={summary.total_alerts} icon={<AlertTriangle size={20} />} />
        <SummaryTile label="Users affected" value={summary.users_affected} icon={<Users size={20} />} />
        <SummaryTile label="Period (days)" value={summary.period_days} icon={<Clock size={20} />} />
      </div>

      {Object.keys(summary.reason_breakdown).length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm">Reason breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              {Object.entries(summary.reason_breakdown).map(([reason, count]) => (
                <div key={reason} className="text-sm">
                  <span className="font-semibold">{reason}:</span> {count}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        {alerts.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground border rounded-lg">
            No alerts in the last {days} days. Cedar reports are healthy. 🌲
          </div>
        ) : (
          alerts.map((a) => (
            <AlertRow key={a.id}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-sm text-muted-foreground">{a.message}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.created_at ? new Date(a.created_at).toLocaleString() : 'unknown time'}
                </div>
              </div>
              <div className="text-xs space-y-1">
                <div>
                  <span className="font-semibold">User:</span> {a.user_id || '—'}
                </div>
                <div>
                  <span className="font-semibold">Reason:</span> {a.reason}
                </div>
                <div>
                  <span className="font-semibold">Detail:</span> {a.detail || '—'}
                </div>
                <div>
                  <span className="font-semibold">Engines active:</span>{' '}
                  {a.engines_active && a.engines_active.length > 0 ? a.engines_active.join(', ') : 'none'}
                </div>
              </div>
            </AlertRow>
          ))
        )}
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const AdminPlanHealthDashboard: React.FC = () => {
  const [tab, setTab] = useState<TabId>('weekly-plan-synth');
  const [days, setDays] = useState<number>(7);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity size={24} /> Plan Health Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            Sunday-morning weekend validation for weekly plan synth + Cedar reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7d</option>
            <option value={14}>Last 14d</option>
            <option value={30}>Last 30d</option>
          </select>
          <button
            onClick={refresh}
            className="border rounded px-3 py-1 text-sm flex items-center gap-1 hover:bg-accent"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 border-b">
        <button
          className={`px-4 py-2 text-sm flex items-center gap-2 ${
            tab === 'weekly-plan-synth' ? 'border-b-2 border-primary font-semibold' : 'text-muted-foreground'
          }`}
          onClick={() => setTab('weekly-plan-synth')}
        >
          <ClipboardList size={16} /> Weekly Plan Synth
        </button>
        <button
          className={`px-4 py-2 text-sm flex items-center gap-2 ${
            tab === 'cedar-report' ? 'border-b-2 border-primary font-semibold' : 'text-muted-foreground'
          }`}
          onClick={() => setTab('cedar-report')}
        >
          <FileText size={16} /> Cedar Report
        </button>
      </div>

      <div key={`${tab}-${days}-${refreshKey}`}>
        {tab === 'weekly-plan-synth' ? <WeeklyPlanSynthTab days={days} /> : <CedarReportTab days={days} />}
      </div>
    </div>
  );
};

export default AdminPlanHealthDashboard;
